import { CapacityLimitReason, Market, MathLib, ORACLE_PRICE_SCALE } from '@morpho-org/blue-sdk'
import { Time } from '@morpho-org/morpho-ts'

/**
 * Forward-accrual window: max-amount math accrues the position to `now + ACCRUAL_BUFFER` so a max
 * built at quote time still holds when the tx lands (the debt keeps accruing until then). Shared by
 * the repay wrap-split and the withdraw-collateral residual so both price the same window.
 */
export const ACCRUAL_BUFFER = Time.s.from.h(2n)

/**
 * The market-actions-v2 max-amount math (WS7), sourced from a blue-sdk `Market`
 * (`getPositionData` / `marketData`) rather than the legacy `simulation-sdk`
 * `SimulationState`. Kept as pure functions so the safety-critical "displayed max
 * never reverts" invariant (S2) is locked by unit tests, independent of React.
 *
 * Both health-sensitive maxes take an explicit `maxLtv` cap from the caller (the MAX-button
 * `getDisplayedMaxLtv` or the higher manual-entry `getBorrowCeilingLtv`), so a max built at that
 * cap stays within what the SDK validates at build time (S2).
 */

/** Clones a `Market` with extra supply liquidity (public-allocator reallocation headroom). */
const withExtraSupplyLiquidity = (market: Market, extraSupplyAssets: bigint): Market =>
  extraSupplyAssets > 0n
    ? new Market({
        params: market.params,
        totalSupplyAssets: market.totalSupplyAssets + extraSupplyAssets,
        totalSupplyShares: market.totalSupplyShares,
        totalBorrowAssets: market.totalBorrowAssets,
        totalBorrowShares: market.totalBorrowShares,
        lastUpdate: market.lastUpdate,
        fee: market.fee,
        price: market.price,
        rateAtTarget: market.rateAtTarget
      })
    : market

/**
 * Max additional borrow (loan assets) for the v2 flow, capped at the tighter of the
 * displayed max-LTV bound and the available liquidity (on-market + public-allocator
 * reallocatable).
 *
 * When the **LTV** is the binding limit, blue-sdk returns the un-padded
 * `wMulDown(collateralValue, maxLtv) − borrowAssets`; borrowing that exact value
 * trips morpho-sdk's `borrowAssets + amount + 1n > maxSafeBorrow` rounding guard and
 * reverts (`BorrowExceedsSafeLtvError`). We subtract `1 wei` in that case so the
 * displayed max never reverts (S2). When **liquidity** binds, the cap is `market
 * liquidity`, so no pad is needed (and none must be added, or the max under-shows).
 *
 * @param params.market - The fresh blue-sdk `Market` (must carry an oracle `price`).
 * @param params.collateral - Current position collateral.
 * @param params.borrowShares - Current position borrow shares.
 * @param params.proposedCollateral - Collateral supplied atomically in a combo (raises the cap).
 * @param params.maxReallocatable - Extra liquidity the public allocator can reallocate in (WS6).
 * @param params.maxLtv - The LTV cap to size against (`getDisplayedMaxLtv` or `getBorrowCeilingLtv`).
 * @returns Max additional borrow assets, or `undefined` when the oracle price is unavailable.
 */
export const computeMaxBorrowAssets = ({
  market,
  collateral,
  borrowShares,
  proposedCollateral = 0n,
  maxReallocatable = 0n,
  maxLtv
}: {
  market: Market
  collateral: bigint
  borrowShares: bigint
  proposedCollateral?: bigint
  maxReallocatable?: bigint
  maxLtv: bigint
}): bigint | undefined => {
  const limit = withExtraSupplyLiquidity(market, maxReallocatable).getBorrowCapacityLimit(
    { collateral: collateral + proposedCollateral, borrowShares },
    { maxLtv }
  )
  if (!limit) return undefined

  return limit.limiter === CapacityLimitReason.collateral
    ? MathLib.zeroFloorSub(limit.value, 1n)
    : limit.value
}

/**
 * Max repay (loan assets) for the v2 flow — ERC20-first `available` balance capped at the
 * position's debt, with the full-close case funded end-to-end.
 *
 * A repay entered at `debt` flips the builder into shares mode (S6), whose transfer is the
 * SDK's **accrual-buffered** pull (`fullCloseNeed` = debt forward-accrued, > `debt`) — not
 * `debt` itself. So MAX may only land on `debt` (full close) when `available` covers
 * `fullCloseNeed`; a wallet holding `available ∈ [debt, fullCloseNeed)` would otherwise open
 * a bundle whose final tx pulls more than the wallet has: any approval/permit step completes
 * first, then the funds-moving tx reverts — gas spent, an allowance left behind.
 * When the buffered pull isn't covered, MAX caps to a partial **assets** repay instead:
 * `min(available, debt − 1)` — strictly below `debt` so shares mode can't trigger, and an
 * assets-mode repay pulls exactly the entered amount, which the wallet holds.
 *
 * @param params.debt - Current position debt (`borrowAssets`).
 * @param params.fullCloseNeed - The SDK's accrual-buffered shares-mode transfer for a full close.
 * @param params.available - Loan assets the wallet can source (ERC20 + wrappable native when wrapping).
 * @returns Max repay amount the flow can actually fund.
 */
export const computeMaxRepayAssets = ({
  debt,
  fullCloseNeed,
  available
}: {
  debt: bigint
  fullCloseNeed: bigint
  available: bigint
}): bigint => {
  if (debt === 0n) return 0n
  if (available >= fullCloseNeed) return debt
  return MathLib.min(available, debt - 1n)
}

/**
 * Max loan-asset (lend) withdraw for the v2 flow: the position's supplied assets,
 * capped at local market liquidity plus Public Allocator liquidity (VRM-5326).
 *
 * Liquidity (`totalSupplyAssets − totalBorrowAssets`) is **accrual-invariant**
 * (interest accrues into both totals equally) and the protocol check is `<=`, so an
 * assets-mode withdraw of exactly the quoted liquidity never reverts from accrual —
 * no rounding pad is applied; a concurrent borrow/withdraw landing first is an
 * honest revert no pad can prevent. Public Allocator capacity raises the asset-denominated MAX
 * but never loosens the exact-shares gate: reallocations are fixed assets and cannot cover
 * interest earned by the shares between quote and inclusion, so the gate below measures local
 * liquidity alone. A plan may still accompany a shares-mode exit — it only adds headroom
 * ({@link shouldUseExactSharesForMaxLendWithdraw}).
 *
 * Exact-shares full exits are allowed only when the position's supply value, forward-accrued to
 * `accrualTimestamp`, still fits within local liquidity. This preserves the dust-free shares path
 * when there is comfortable headroom, while falling back to assets mode when thin headroom could
 * be consumed by interest before inclusion. Zero-borrow markets do not accrue, so equality safely
 * allows shares mode.
 *
 * @param params.supplyAssets - The position's current supplied loan assets (accrued).
 * @param params.market - Market state at the quote snapshot.
 * @param params.supplyShares - The position's current supply shares.
 * @param params.accrualTimestamp - Execution horizon to accrue the position to.
 * @param params.maxReallocatable - Extra assets the Public Allocator can move into this market.
 * @returns Max withdrawable assets, whether liquidity lowers that max, and whether an exact-shares full exit is safe.
 */
export const computeMaxLendWithdrawAssets = ({
  supplyAssets,
  market,
  supplyShares,
  accrualTimestamp,
  maxReallocatable = 0n
}: {
  supplyAssets: bigint
  market: Market
  supplyShares: bigint
  accrualTimestamp: bigint
  maxReallocatable?: bigint
}): {
  maxWithdrawAssets: bigint
  isLiquidityCapped: boolean
  canFullExitWithExactShares: boolean
} => {
  const { totalSupplyAssets, totalBorrowAssets } = market
  const localLiquidity = MathLib.zeroFloorSub(totalSupplyAssets, totalBorrowAssets)
  const availableLiquidity = localLiquidity + maxReallocatable
  const isLiquidityCapped = availableLiquidity < supplyAssets
  const canFullExitWithExactShares =
    totalBorrowAssets === 0n ||
    market
      .accrueInterest(MathLib.max(accrualTimestamp, market.lastUpdate))
      .toSupplyAssets(supplyShares) <= localLiquidity

  return {
    maxWithdrawAssets: isLiquidityCapped ? availableLiquidity : supplyAssets,
    isLiquidityCapped,
    canFullExitWithExactShares
  }
}

/**
 * Max collateral withdrawable for the v2 flow while staying within the displayed max-LTV
 * (`getDisplayedMaxLtv`), so the displayed max never reverts at build time (S2). Two repay modes:
 *
 * - `isMaxRepay` (full close): every share is burned, so no debt remains and all collateral is free.
 * - partial repay: only `proposedLoanRepay`'s worth of shares clears. The residual is sized on the
 *   market forward-accrued to `accrualTimestamp`, since the loan keeps accruing until the tx lands and
 *   morpho-sdk checks health against the accrued debt — a quote-block residual would leave the kept
 *   collateral one tick short and revert `WithdrawMakesPositionUnhealthy`.
 *
 * @param params.market - The fresh blue-sdk `Market` (must carry an oracle `price`).
 * @param params.collateral - Current position collateral.
 * @param params.borrowShares - Current position borrow shares.
 * @param params.proposedLoanRepay - Loan assets repaid in a partial combo (frees collateral).
 * @param params.isMaxRepay - The combo fully closes the debt (shares-mode) ⇒ all collateral is free.
 * @param params.maxLtv - The LTV cap to size against (`getDisplayedMaxLtv` or `getBorrowCeilingLtv`).
 * @param params.accrualTimestamp - Execution horizon to accrue to (clamped ≥ `market.lastUpdate`).
 * @returns Max withdrawable collateral assets, or `undefined` when the oracle price is unavailable.
 */
export const computeMaxWithdrawCollateralAssets = ({
  market,
  collateral,
  borrowShares,
  proposedLoanRepay = 0n,
  isMaxRepay = false,
  maxLtv,
  accrualTimestamp
}: {
  market: Market
  collateral: bigint
  borrowShares: bigint
  proposedLoanRepay?: bigint
  isMaxRepay?: boolean
  maxLtv: bigint
  accrualTimestamp: bigint
}): bigint | undefined => {
  // Accrue to the execution horizon (clamped ≥ lastUpdate) so the residual matches on-chain state.
  const projected = market.accrueInterest(MathLib.max(accrualTimestamp, market.lastUpdate))

  // Full close burns every share; a partial repay clears only its assets' worth (rounded down).
  const repaidShares = isMaxRepay ? borrowShares : projected.toBorrowShares(proposedLoanRepay)
  const borrowSharesAfter = MathLib.zeroFloorSub(borrowShares, repaidShares)

  const limit = projected.getWithdrawCollateralCapacityLimit(
    { collateral, borrowShares: borrowSharesAfter },
    { maxLtv }
  )
  if (!limit) return undefined

  // No residual debt (full close, or nothing borrowed) → all collateral is free.
  const residualDebt = projected.toBorrowAssets(borrowSharesAfter)
  if (residualDebt === 0n || projected.price == null) return limit.value

  // Reserve collateral whose floored value backs the residual at maxLtv (ceil each step).
  const requiredValue = MathLib.wDivUp(residualDebt, maxLtv)
  const requiredCollateral = MathLib.mulDivUp(requiredValue, ORACLE_PRICE_SCALE, projected.price)
  const safeMaxWithdraw = MathLib.zeroFloorSub(collateral, requiredCollateral)

  return MathLib.min(limit.value, safeMaxWithdraw)
}
